import { useEffect } from "react";
import LegalLayout from "@/components/LegalLayout";
import { EnvelopeSimple } from "@phosphor-icons/react";

export default function Contact() {
  useEffect(() => {
    document.title = "Contact · Stable-ish";
    window.scrollTo(0, 0);
  }, []);

  return (
    <LegalLayout title="Get in touch" eyebrow="§ contact">
      <p>
        Stable-ish is built and operated by a small team of working ER and ICU
        clinicians. We answer every message ourselves.
      </p>

      <div className="not-prose mt-10 grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
        <a
          href="mailto:hello@stableish.me"
          className="group bg-black p-6 md:p-8 hover:bg-[#0a0a0a] transition-colors block"
          data-testid="contact-email-card"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 grid place-items-center bg-[#FF2A2A]/10 border border-[#FF2A2A]/40 text-[#FF2A2A]">
              <EnvelopeSimple size={18} weight="duotone" />
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/40">
              general · partnerships · feedback
            </span>
          </div>
          <p className="font-display text-2xl font-extrabold tracking-tight group-hover:text-[#FF2A2A] transition-colors">
            hello@stableish.me
          </p>
        </a>
        <div className="bg-black p-6 md:p-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A]">
              safety concerns
            </span>
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            Spotted something clinically wrong, dangerous, or unsafe in an
            answer? Email us with the screenshot and the question — we&apos;ll
            review within 24 hours.
          </p>
          <a
            href="mailto:hello@stableish.me?subject=Safety%20concern"
            className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-white hover:text-[#FF2A2A]"
          >
            hello@stableish.me →
          </a>
        </div>
      </div>

      <h2>Subscription &amp; billing FAQ</h2>

      <h3>How do I cancel my subscription?</h3>
      <p>
        Open the <strong>Settings</strong> app on your iPhone → tap your name
        at the top → tap <strong>Subscriptions</strong> → tap{" "}
        <strong>Stable-ish</strong> → tap <strong>Cancel Subscription</strong>.
        Your access continues until the end of the current billing period.
        Uninstalling the app does <em>not</em> cancel your subscription.
      </p>

      <h3>What&rsquo;s included in the subscription?</h3>
      <p>
        Full access to all 50+ clinical tools — including the AI clinical
        answer engine, weight-based Peds Dose Calc, Parkland burn fluid
        formula, COWS &amp; alcohol withdrawal scoring, ECG interpretation,
        the drug-drug interaction checker, and the 5 core bedside
        calculators (GCS, CrCl, anion gap, corrections, IBW/ABW). Plus every
        tool we add during your subscription.
      </p>

      <h3>What happens after the 14-day free trial?</h3>
      <p>
        On day 14, your Apple ID will be automatically charged for the plan
        you selected — <strong>$9.99/month</strong> or{" "}
        <strong>$79.99/year</strong> — unless you cancel first. You can
        cancel any time during the trial from{" "}
        <strong>Settings → Subscriptions</strong> and you won&rsquo;t be
        charged.
      </p>

      <h3>What is the Founding Member Rate?</h3>
      <p>
        If you subscribe during our launch period (monthly $9.99 or yearly
        $79.99), that price is locked in for as long as your subscription
        stays continuous. When we raise prices for new subscribers in the
        future, your rate stays the same — enforced by Apple&rsquo;s
        subscription price-lock policy. If your subscription lapses and you
        resubscribe later, the then-current standard price applies.
      </p>

      <h3>How do I restore my subscription on a new device?</h3>
      <p>
        Sign in to the new device with the same Apple ID you used to
        subscribe, install Stable-ish, then tap{" "}
        <strong>Restore Purchases</strong> on the paywall screen. Your
        subscription becomes active on the new device within seconds.
      </p>

      <h3>Can I get a refund?</h3>
      <p>
        Refund requests for App Store purchases are handled directly by
        Apple. Go to{" "}
        <a
          href="https://reportaproblem.apple.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          reportaproblem.apple.com
        </a>{" "}
        and sign in with your Apple ID → find the Stable-ish purchase →
        follow the prompts. Approval is at Apple&rsquo;s discretion. We
        don&rsquo;t have the ability to issue refunds directly, but if you
        run into trouble we&rsquo;re happy to help you through the process.
      </p>

      <h3>Data privacy questions?</h3>
      <p>
        See our full{" "}
        <a href="/privacy" className="text-[#FF2A2A]">
          Privacy Policy
        </a>{" "}
        or write to{" "}
        <a href="mailto:privacy@stableish.me">privacy@stableish.me</a>. For
        California-specific requests (CCPA/CPRA), we respond within 45 days.
      </p>

      <h2>Response time</h2>
      <p>
        We answer every email within <strong>2 business days</strong>. Safety
        concerns are prioritized and reviewed within{" "}
        <strong>24 hours</strong>.
      </p>

      <h2>Press &amp; media</h2>
      <p>
        We&apos;re pre-launch and don&apos;t have a press kit yet. For media inquiries,
        write to{" "}
        <a href="mailto:hello@stableish.me">hello@stableish.me</a> and we&apos;ll
        respond directly.
      </p>
    </LegalLayout>
  );
}
